import { env } from '../config/env'
import { ENABLE_MOCK_AUTH, MOCK_INIT_DATA, MOCK_USER } from '../config/dev-mocks'

/**
 * Получает актуальный initData из Telegram WebApp
 * Важно использовать это при каждом запросе, так как initData может устареть
 */
function getActualInitData(): string {
  if (ENABLE_MOCK_AUTH) {
    return MOCK_INIT_DATA
  }
  
  if (typeof window !== 'undefined' && (window as any).Telegram?.WebApp?.initData) {
    return (window as any).Telegram.WebApp.initData
  }
  
  return ''
}

/**
 * HTTP клиент для работы с API
 * 
 * Универсальная функция для выполнения HTTP запросов к API серверу.
 * Автоматически добавляет необходимые заголовки и обрабатывает ошибки.
 * В режиме моков возвращает моковые данные вместо реальных запросов.
 * 
 * @template T - Тип возвращаемых данных
 * @param endpoint - Endpoint API (путь относительно базового URL)
 * @param options - Опции запроса (RequestInit + initData для аутентификации)
 * @param options.initData - initData от Telegram для аутентификации (добавляется в заголовок X-Telegram-Init-Data)
 * @returns Promise с данными ответа типа T
 * 
 * @throws {Error} Выбрасывает ошибку при неуспешном ответе (status !== 200-299)
 * 
 * @example
 * ```ts
 * // GET запрос
 * const products = await apiClient<Product[]>('/api/tea')
 * 
 * // POST запрос с телом
 * const order = await apiClient<Order>('/api/orders', {
 *   method: 'POST',
 *   body: JSON.stringify({ productId: '1', quantity: 2 }),
 * })
 * 
 * // Запрос с аутентификацией
 * const { initData } = useTelegram()
 * const cart = await apiClient<Cart>('/api/user/cart', { initData })
 * ```
 */
export async function apiClient<T>(
  endpoint: string,
  options?: RequestInit & {
    initData?: string
  }
): Promise<T> {
  const { initData, ...fetchOptions } = options || {}

  // В режиме моков возвращаем моковые данные для защищенных эндпоинтов
  if (ENABLE_MOCK_AUTH && initData && endpoint.includes('/api/user/')) {
    // Моковые ответы для различных эндпоинтов
    if (endpoint === '/api/user/me') {
      return { data: MOCK_USER } as T
    }

    if (endpoint === '/api/user/settings') {
      return {
        success: true,
        data: {
          notificationsEnabled: true,
          orderUpdates: true,
          eventReminders: true,
          promotions: false,
          preferredLanguage: 'ru',
          theme: 'auto',
          newProducts: true,
          newEvents: true,
          diaryPrompts: true,
          cartReminders: true,
        },
      } as T
    }
    
    if (endpoint === '/api/user/cart') {
      return {
        data: {
          items: [],
          itemCount: 0,
          total: 0,
        },
      } as T
    }
    
    if (endpoint.includes('/api/user/orders')) {
      return {
        data: [],
        meta: {
          pagination: {
            page: 1,
            limit: 20,
            total: 0,
            totalPages: 0,
            hasNext: false,
            hasPrev: false,
          },
        },
      } as T
    }
    
    // Моковые записи дневника
    if (endpoint.includes('/api/user/diary')) {
      // Для статистики
      if (endpoint.includes('/stats')) {
        return {
          success: true,
          data: {
            totalEntries: 3,
            uniqueTeas: 2,
            averageRating: 4.3,
            favoriteTeaId: null,
            favoriteTeaName: null,
          },
        } as T
      }
      
      // Для списка записей
      return {
        success: true,
        data: [
          {
            id: 'mock-1',
            userId: MOCK_USER.id,
            teaId: null,
            teaName: 'Дянь Хун',
            photoUrl: null,
            rating: 5,
            notes: 'Отличный красный чай с медовым вкусом',
            mood: 'relaxed',
            brewingMethod: 'gongfu',
            tasteNotes: ['мёд', 'сухофрукты'],
            drankAt: new Date().toISOString(),
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
          {
            id: 'mock-2',
            userId: MOCK_USER.id,
            teaId: null,
            teaName: 'Те Гуань Инь',
            photoUrl: null,
            rating: 4,
            notes: 'Свежий улун с цветочным ароматом',
            mood: 'focused',
            brewingMethod: 'gongfu',
            tasteNotes: ['цветы', 'сливки'],
            drankAt: new Date(Date.now() - 86400000).toISOString(),
            createdAt: new Date(Date.now() - 86400000).toISOString(),
            updatedAt: new Date(Date.now() - 86400000).toISOString(),
          },
        ],
      } as T
    }
    
    // Моковые уведомления о событиях
    if (endpoint.includes('/api/user/event-notifications')) {
      if (endpoint.includes('/preferences')) {
        return {
          success: true,
          data: { eventReminders: true },
        } as T
      }
      if (endpoint.includes('/deliveries')) {
        return {
          success: true,
          data: [], // По умолчанию пустой список напоминаний
        } as T
      }
    }

    if (endpoint.includes('/api/user/events/') && endpoint.includes('/reminders')) {
      return {
        success: true,
        data: { success: true },
      } as T
    }

    // Для других защищенных эндпоинтов возвращаем пустой объект
    return {} as T
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(fetchOptions.headers as Record<string, string> || {}),
  }

  // Добавляем initData в заголовки для аутентификации
  // ВАЖНО: Используем актуальный initData из window, а не переданный
  // Это предотвращает 401 ошибки когда initData устарел в контексте React
  const authInitData = initData ? getActualInitData() : undefined
  if (authInitData) {
    const isVk = typeof window !== 'undefined' && (
      (window as any).Telegram?.WebApp?.isVk === true || 
      window.location.search.includes('vk_app_id')
    )
    if (isVk) {
      headers['X-VK-Init-Data'] = authInitData
      
      const webApp = (window as any).Telegram?.WebApp
      const user = webApp?.initDataUnsafe?.user
      if (user) {
        const vkUserInfo = {
          first_name: user.first_name,
          last_name: user.last_name,
          username: user.username,
          photo_url: user.photo_url,
        }
        headers['X-VK-User-Info'] = encodeURIComponent(JSON.stringify(vkUserInfo))
      }
    } else {
      headers['X-Telegram-Init-Data'] = authInitData
    }
  }

  const response = await fetch(`${env.apiUrl}${endpoint}`, {
    ...fetchOptions,
    headers,
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      message: 'Произошла ошибка',
    }))
    console.error('[API Client] Error response:', {
      status: response.status,
      endpoint,
      error
    })
    throw new Error(error.error || error.message || `HTTP error! status: ${response.status}`)
  }

  return response.json()
}

